import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColorArea,
  ColorField,
  ColorSlider,
  ColorThumb,
  Dialog,
  DialogTrigger,
  ColorPicker as HeadlessColorPicker,
  Input,
  parseColor,
  SliderTrack,
  useLocale,
  type Color,
} from "react-aria-components";
import { getTypographyClassName } from "@appsmith/wds-theming";

// Relative imports (not the "@appsmith/wds" barrel) keep this component out
// of the package-barrel dependency cycle counted by the CI cyclic-deps gate.
import { inputFieldStyles } from "../../Field";
import { fieldErrorStyles } from "../../FieldError";
import { FieldLabel } from "../../FieldLabel";
import { textInputStyles } from "../../Input";
import { Popover, useRootContainer } from "../../Popover";
import { Text } from "../../Text";

import styles from "./styles.module.css";
import type { ColorPickerProps } from "./types";
import { ColorPickerTrigger } from "./ColorPickerTrigger";

/**
 * Unset state: fully transparent — the checkerboard underlay shows through
 * the swatch and the trigger reads "transparent" (mirrors RAC's own
 * alpha-zero swatch naming).
 */
const UNSET_COLOR = "#00000000";

/** try/catch normalizer — an unparseable value degrades to unset, never throws at render. */
const tryParseColor = (value: string | undefined): Color | undefined => {
  if (value === undefined || value === "") return undefined;

  try {
    return parseColor(value);
  } catch {
    return undefined;
  }
};

/**
 * ColorPicker wraps RAC's color family — swatch-trigger button opening a
 * popover with ColorArea (saturation/brightness), a hue ColorSlider, an
 * alpha ColorSlider gated on `allowAlpha`, and a ColorField hex entry — in
 * the Field chrome idiom of Datepicker/DateRangePicker.
 *
 * Live preview stays in RAC-local state (the shared HeadlessColorPicker
 * state); the external `onChange` fires only on drag END (onChangeEnd) and
 * on hex-field commit, so meta updates never storm during a drag.
 */
export const ColorPicker = (props: ColorPickerProps) => {
  const {
    allowAlpha = false,
    className,
    contextualHelp,
    errorMessage,
    isDisabled,
    isLoading,
    isRequired,
    label,
    onChange,
    popoverClassName,
    size = "medium",
    value,
  } = props;
  const root = useRootContainer();
  const { locale } = useLocale();
  const format = allowAlpha ? "hexa" : "hex";
  const committedColor = useMemo(() => tryParseColor(value), [value]);

  // Live preview state — RAC's shared picker state drives the area/sliders/
  // field and the swatch continuously; only commits leave the component.
  const [color, setColor] = useState<Color>(
    () => committedColor ?? parseColor(UNSET_COLOR),
  );

  useEffect(
    function syncCommittedColorIntoPreview() {
      setColor(committedColor ?? parseColor(UNSET_COLOR));
    },
    [committedColor],
  );

  const commit = useCallback(
    (nextColor: Color) => {
      onChange?.(nextColor.toString(format));
    },
    [onChange, format],
  );

  // ColorField fires onChange only on commit (BLUR in RAC 1.7 — Enter does
  // not commit); a cleared or unparseable entry yields null, which we
  // ignore (the field reverts).
  const handleFieldCommit = useCallback(
    (nextColor: Color | null) => {
      if (nextColor) commit(nextColor);
    },
    [commit],
  );

  const isUnset = color.getChannelValue("alpha") === 0;
  const colorName = isUnset ? "transparent" : color.getColorName(locale);
  const hexText = committedColor ? committedColor.toString(format) : "";

  return (
    <HeadlessColorPicker onChange={setColor} value={color}>
      <div
        className={clsx(inputFieldStyles.field, className)}
        data-disabled={Boolean(isDisabled) ? "" : undefined}
        data-field=""
        data-size={size}
      >
        <FieldLabel
          contextualHelp={contextualHelp}
          isDisabled={isDisabled}
          isRequired={isRequired}
        >
          {label}
        </FieldLabel>
        <DialogTrigger>
          <ColorPickerTrigger
            colorName={colorName}
            hexText={hexText}
            isDisabled={isDisabled}
            isLoading={isLoading}
            size={size}
          />
          <Popover
            UNSTABLE_portalContainer={root}
            className={clsx(styles.popover, popoverClassName)}
          >
            <Dialog aria-label="Color picker" className={styles.dialog}>
              <ColorArea
                className={styles.area}
                colorSpace="hsb"
                onChangeEnd={commit}
                xChannel="saturation"
                yChannel="brightness"
              >
                <ColorThumb className={styles.thumb} />
              </ColorArea>
              <ColorSlider
                channel="hue"
                className={styles.slider}
                colorSpace="hsb"
                onChangeEnd={commit}
              >
                <SliderTrack className={styles.sliderTrack}>
                  <ColorThumb className={styles.thumb} />
                </SliderTrack>
              </ColorSlider>
              {allowAlpha && (
                <ColorSlider
                  channel="alpha"
                  className={styles.slider}
                  onChangeEnd={commit}
                >
                  <SliderTrack
                    className={clsx(styles.sliderTrack, styles.alphaTrack)}
                  >
                    <ColorThumb className={styles.thumb} />
                  </SliderTrack>
                </ColorSlider>
              )}
              <ColorField
                aria-label="Hex color"
                className={styles.hexField}
                onChange={handleFieldCommit}
              >
                <div className={textInputStyles.inputGroup}>
                  <Input
                    className={clsx(
                      textInputStyles.input,
                      getTypographyClassName("body"),
                    )}
                    data-size={size}
                  />
                </div>
              </ColorField>
            </Dialog>
          </Popover>
        </DialogTrigger>
        {/* The MultiSelect precedent: RAC's ColorPicker is not a field root,
            so FieldErrorContext never exists here — render the error line
            manually with the shared FieldError styles. */}
        {Boolean(errorMessage) && (
          <div className={fieldErrorStyles.errorText}>
            <Text color="negative" size="caption">
              {errorMessage}
            </Text>
          </div>
        )}
      </div>
    </HeadlessColorPicker>
  );
};
